<script setup lang="ts">
import type { CoinGeckoSelection } from '#core/utils/coingecko-selection'
import type { ComboboxContentProps } from '@forgd/ui-kit/combobox'
import type { SelectTriggerSize } from '@forgd/ui-kit/select'
import type { HTMLAttributes } from 'vue'
import { COINGECKO_NOT_LISTED_SELECTION } from '#core/utils/coingecko-selection'
import { NoSearchResults } from '@forgd/ui-kit/blocks/empty-states'
import { Button } from '@forgd/ui-kit/button'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@forgd/ui-kit/combobox'
import { cn } from '@forgd/ui-kit/lib/utils'
import { MagnifyingGlassIcon } from '@heroicons/vue/20/solid'
import { ArrowRightIcon, XMarkIcon } from '@heroicons/vue/24/outline'

export type CoinSearchResult = SuccessResponseBody<'/coins/search'>['coins'][number]

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{
  size?: SelectTriggerSize
  placeholder?: string
  searchPlaceholder?: string
  class?: HTMLAttributes['class']
  contentClass?: HTMLAttributes['class']
  showNotListedAction?: boolean
  error?: boolean
  disabled?: boolean
  align?: ComboboxContentProps['align']
  side?: ComboboxContentProps['side']
  sideOffset?: ComboboxContentProps['sideOffset']
  contentWidth?: string
}>(), {
  size: 'md',
  placeholder: 'Search token',
  searchPlaceholder: 'Type at least 2 characters to start searching',
  showNotListedAction: true,
  align: 'start',
  side: 'bottom',
  sideOffset: 4,
  contentWidth: '360px',
})

const model = defineModel<CoinGeckoSelection | null | undefined>()

const { open, query, debouncedQuery, filtered, loading, reset } = useAsyncComboboxFilter<CoinSearchResult>(
  async (q) => {
    const result = await apiFetch('/coins/search', {
      params: { query: { query: q } },
    })
    return result?.coins ?? []
  },
)

const hasSelection = computed(() => !!model.value?.name)

watch(hasSelection, (selected) => {
  if (selected) {
    open.value = false
  }
})

function selectCoin(coin: CoinSearchResult) {
  model.value = coin
  reset()
}

function clearSelection() {
  model.value = undefined
  reset()
}

function selectNotListed() {
  model.value = { ...COINGECKO_NOT_LISTED_SELECTION }
  reset()
}

function onOpenChange(value: boolean) {
  if (value && hasSelection.value) {
    return
  }
  open.value = value
}

const contentStyle = computed(() => ({
  width: props.contentWidth,
  minWidth: props.contentWidth,
}))

const sizeClasses = {
  xs: { img: 'size-4', name: 'text-xs', symbol: 'text-[10px]' },
  sm: { img: 'size-5', name: 'text-sm', symbol: 'text-xs' },
  md: { img: 'size-6', name: 'text-base', symbol: 'text-sm' },
  lg: { img: 'size-6', name: 'text-base', symbol: 'text-sm' },
  xl: { img: 'size-8', name: 'text-lg', symbol: 'text-base' },
} as const satisfies Record<SelectTriggerSize, { img: string, name: string, symbol: string }>

const sizeClass = computed(() => sizeClasses[props.size])
</script>

<template>
  <Combobox
    :open="open"
    class="relative w-full"
    data-ui="CoinGeckoSearch"
    @update:open="onOpenChange"
  >
    <div class="relative">
      <ComboboxTrigger
        v-bind="$attrs"
        :size="size"
        :error="error"
        :disabled="disabled"
        :placeholder="placeholder"
        :class="cn('w-full', props.class, hasSelection && 'bg-white border-default text-sm pe-10')"
        @pointerdown="hasSelection && $event.preventDefault()"
      >
        <div v-if="hasSelection" class="flex w-full items-center justify-between gap-2">
          <div class="flex min-w-0 items-center gap-2">
            <img
              v-if="model?.image"
              :src="model.image"
              :class="cn('shrink-0 rounded-full', sizeClass.img)"
              loading="lazy"
              alt=""
            >
            <span :class="cn('truncate font-semibold text-primal', sizeClass.name)">
              {{ model?.name }}
            </span>
            <span v-if="model?.symbol" :class="cn('shrink-0 uppercase text-chart-label', sizeClass.symbol)">
              {{ model.symbol }}
            </span>
          </div>
        </div>
        <template #icon>
          <MagnifyingGlassIcon v-if="!hasSelection" class="size-5 shrink-0 opacity-50" />
          <span v-else class="sr-only" aria-hidden="true" />
        </template>
      </ComboboxTrigger>
      <Button
        v-if="hasSelection && !disabled"
        variant="ghost"
        size="sm"
        icon-only
        aria-label="Clear token selection"
        class="absolute inset-e-1 top-1/2 -translate-y-1/2"
        @click="clearSelection"
      >
        <XMarkIcon aria-hidden="true" />
      </Button>
    </div>

    <ComboboxContent
      :align="align"
      :side="side"
      :side-offset="sideOffset"
      :style="contentStyle"
      :class="cn(
        'flex max-h-[min(26rem,70vh)] flex-col overflow-hidden p-0 !w-auto max-w-[calc(100vw-2rem)]',
        contentClass,
      )"
    >
      <ComboboxInput
        v-model="query"
        :loading="loading"
        type="search"
        role="searchbox"
        class="shrink-0"
        :placeholder="searchPlaceholder"
      />
      <ComboboxList :scroll-hint="false" class="!max-h-[min(13rem,calc(70vh-12rem))] overflow-y-auto">
        <ComboboxEmpty
          v-if="loading"
          class="items-start justify-start px-2 py-1.5 text-left text-placeholder"
        >
          Loading...
        </ComboboxEmpty>
        <ComboboxEmpty
          v-else-if="filtered.length === 0 && debouncedQuery.length < 2"
          class="items-start justify-start px-2 py-1.5 text-left text-placeholder"
        >
          No options
        </ComboboxEmpty>
        <ComboboxEmpty v-else-if="filtered.length === 0" class="py-0">
          <NoSearchResults
            title="Sorry!"
            message="There are no results to show based on your search. Please try a different query."
            @clear="query = ''"
          />
        </ComboboxEmpty>
        <template v-else>
          <ComboboxItem
            v-for="coin in filtered"
            :key="coin.id"
            :value="coin.id"
            :selected="model?.id === coin.id"
            class="gap-2"
            @select="selectCoin(coin)"
          >
            <img
              v-if="coin.image"
              :src="coin.image"
              :class="cn('shrink-0 rounded-full', sizeClass.img)"
              loading="lazy"
              alt=""
            >
            <span :class="cn('font-semibold text-primal', sizeClass.name)">
              {{ coin.name }}
            </span>
            <span :class="cn('uppercase text-secondary', sizeClass.symbol)">
              {{ coin.symbol }}
            </span>
          </ComboboxItem>
        </template>
      </ComboboxList>
      <div
        v-if="showNotListedAction"
        class="shrink-0 border-t border-default bg-white p-2"
      >
        <Button
          variant="ghost"
          type="button"
          class="w-full justify-center"
          @click="selectNotListed"
        >
          <span class="text-sm text-primal">
            My token is not listed yet. I'm only exploring
          </span>
          <ArrowRightIcon aria-hidden="true" />
        </Button>
      </div>
    </ComboboxContent>
  </Combobox>
</template>
